<script lang="ts" setup>
import { useI18n } from 'vue-i18n'
import { computed, InputHTMLAttributes, ref, useAttrs, watch } from 'vue'
import dayjs from 'dayjs'
import {
  BACKEND_DATE_FORMAT,
  BACKEND_DATE_FORMAT_2,
  DATE_FORMAT_STRING,
} from '/@src/constants'

export interface IDSKDateInputSeparatedProps extends InputHTMLAttributes {
  id: string
  label?: string
  hint?: string
  hintId?: string
  /** Error message text, also serves as a flag whether the input is valid. */
  errorMessage?: string
  errorMessageId?: string
  readOnly?: boolean
  modelValue?: string
  required?: boolean
  /** Stale zobrazuje "date", ale hodnotu propaguje ako datetime. */
  mode?: 'date' | 'datetime'
  dataTestId?: string
}

const { t } = useI18n()
const attrs = useAttrs()

const props = withDefaults(defineProps<IDSKDateInputSeparatedProps>(), {
  label: undefined,
  hint: undefined,
  hintId: undefined,
  errorMessage: undefined,
  errorMessageId: undefined,
  type: 'text',
  required: false,
  modelValue: undefined,
  mode: 'datetime',
  dataTestId: '',
})

const DATE_NUMBER_PATTERN = '[0-9]*'

const day = ref<string>('')
const month = ref<string>('')
const year = ref<string>('')

const emits = defineEmits(['update:modelValue'])
const isValid = computed<boolean>(() => !props.errorMessage)

function handleOnChange(type: string, event: any) {
  switch (type) {
    case 'day':
      return (day.value = event.target.value)
    case 'month':
      return (month.value = event.target.value)
    case 'year':
      return (year.value = event.target.value)
  }
}

function handleOnBlur(...args: any) {
  const dateValue = [
    year.value,
    month.value.padStart(2, '0'),
    day.value.padStart(2, '0'),
  ].join('-')
  const parsedValue = dayjs(dateValue, DATE_FORMAT_STRING, true)
  const FORMAT = props.mode === 'datetime' ? BACKEND_DATE_FORMAT_2 : DATE_FORMAT_STRING

  emits('update:modelValue', parsedValue.isValid() ? parsedValue.format(FORMAT) : '')
  // @ts-ignore
  attrs.onBlur(...args)
}

watch(
  () => props.modelValue,
  () => {
    try {
      let parsedDate = undefined
      if (props.mode === 'date') {
        parsedDate = dayjs(props.modelValue, DATE_FORMAT_STRING, true)
        if (!parsedDate.isValid()) {
          return
        }
      } else {
        parsedDate = dayjs(
          props.modelValue,
          [BACKEND_DATE_FORMAT, BACKEND_DATE_FORMAT_2],
          true
        )
        if (!parsedDate.isValid()) {
          return
        }
      }

      day.value = parsedDate.format('D')
      month.value = parsedDate.format('M')
      year.value = parsedDate.format('YYYY')
    } catch (e) {
      // noop
    }
  },
  {
    // Evaluate on component load.
    immediate: true,
  }
)
</script>

<template>
  <div
    :class="[
      'govuk-form-group',
      {
        ['govuk-form-group--error']: !isValid,
      },
    ]"
  >
    <IDSKFieldCaption
      :label="label"
      :for="`${id}-day`"
      :hint="hint"
      :hint-id="hintId"
      :error-message="errorMessage"
      :error-message-id="errorMessageId"
    />
    <div :id="id" class="govuk-date-input">
      <div class="govuk-date-input__item">
        <div class="govuk-form-group">
          <label class="govuk-label govuk-date-input__label" :for="`${id}-day`">
            {{ t('globalne.den') }}
          </label>
          <input
            :id="`${id}-day`"
            v-model="day"
            :class="[
              'govuk-input',
              'govuk-date-input__input',
              'govuk-input--width-2',
              {
                'govuk-input--error': !isValid,
              },
            ]"
            :required="required"
            :name="`${id}-day`"
            type="number"
            :pattern="DATE_NUMBER_PATTERN"
            :data-test-id="
              props.id
                ? 'date-separete-input' + '-' + `${id}-day`
                : 'date-separete-input' + `${dataTestId}-day`
            "
            @input="(event) => handleOnChange('day', event)"
            @blur="handleOnBlur"
          />
        </div>
      </div>

      <div class="govuk-date-input__item">
        <div class="govuk-form-group">
          <label class="govuk-label govuk-date-input__label" :for="`${id}-month`">
            {{ t('globalne.mesiac') }}
          </label>
          <input
            :id="`${id}-month`"
            v-model="month"
            :class="[
              'govuk-input',
              'govuk-date-input__input',
              'govuk-input--width-2',
              {
                'govuk-input--error': !isValid,
              },
            ]"
            :required="required"
            :name="`${id}-month`"
            type="number"
            :pattern="DATE_NUMBER_PATTERN"
            :data-test-id="
              props.id
                ? 'date-separete-input' + '-' + `${id}-month`
                : 'date-separete-input' + `${dataTestId}-month`
            "
            @input="(event) => handleOnChange('month', event)"
            @blur="handleOnBlur"
          />
        </div>
      </div>

      <div class="govuk-date-input__item">
        <div class="govuk-form-group">
          <label class="govuk-label govuk-date-input__label" :for="`${id}-year`">
            {{ t('globalne.rok') }}
          </label>
          <input
            :id="`${id}-year`"
            v-model="year"
            :class="[
              'govuk-input',
              'govuk-date-input__input',
              'govuk-input--width-3',
              {
                'govuk-input--error': !isValid,
              },
            ]"
            :required="required"
            :name="`${id}-year`"
            type="number"
            :pattern="DATE_NUMBER_PATTERN"
            :data-test-id="
              props.id
                ? 'date-separete-input' + '-' + `${id}-year`
                : 'date-separete-input' + `${dataTestId}-year`
            "
            @input="(event) => handleOnChange('year', event)"
            @blur="handleOnBlur"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<style lang="scss" scoped>
.govuk-input {
  transition: all 0.195s ease-in;
  padding-left: 10px;

  &:focus {
    z-index: 3;
    border: 2px solid govuk-colour('black');
    box-shadow: inset 0 0 0 3px;
  }

  &:hover {
    margin-right: 0px;
    box-shadow: inset 0 0 0 3px;
  }
}

.govuk-date-input__item {
  &:focus-within,
  &:hover {
    .govuk-label {
      font-weight: 600;
    }

    .govuk-hint {
      color: govuk-colour('black');
    }
  }
}
</style>
